import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Typography,
  Box,
  Paper,
  Divider
} from '@mui/material';
import {
  Event as EventIcon,
  Repeat as RepeatIcon,
  EventNote as EventNoteIcon
} from '@mui/icons-material';

interface RecurringShiftEditModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (choice: 'this' | 'future') => void;
  shiftInfo?: {
    title?: string;
    startDateTime?: string;
    isRecurrenceParent?: boolean;
    hasChildren?: boolean;
  };
}

const RecurringShiftEditModal: React.FC<RecurringShiftEditModalProps> = ({
  open,
  onClose,
  onConfirm,
  shiftInfo
}) => {
  const [selectedOption, setSelectedOption] = React.useState<'this' | 'future'>('this');

  const handleConfirm = () => {
    onConfirm(selectedOption);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  React.useEffect(() => {
    if (open) {
      setSelectedOption('this'); // Reset to default when modal opens
    }
  }, [open]);

  const formatDateTime = (dateTime?: string) => {
    if (!dateTime) return '';
    return new Date(dateTime).toLocaleString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 40px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <RepeatIcon color="primary" />
          <Typography variant="h6" fontWeight="600">
            Edit Recurring Shift
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        {/* Shift Info Section */}
        <Paper 
          elevation={0} 
          sx={{ 
            bgcolor: 'background.default', 
            p: 2, 
            mb: 3, 
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1
          }}
        >
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Selected Shift
          </Typography>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <EventIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight="500">
              {shiftInfo?.title || 'Shift'}
            </Typography>
          </Box>
          {shiftInfo?.startDateTime && (
            <Typography variant="body2" color="text.secondary">
              {formatDateTime(shiftInfo.startDateTime)}
            </Typography>
          )}
        </Paper>

        <Typography variant="body1" gutterBottom sx={{ mb: 3 }}>
          This shift is part of a recurring series. Choose how you want to edit it:
        </Typography>

        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            value={selectedOption}
            onChange={(e) => setSelectedOption(e.target.value as 'this' | 'future')}
          >
            {/* Edit Only This Occurrence */}
            <Paper 
              elevation={0}
              sx={{
                border: '2px solid',
                borderColor: selectedOption === 'this' ? 'primary.main' : 'divider',
                borderRadius: 2,
                p: 2,
                mb: 2,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover'
                }
              }}
              onClick={() => setSelectedOption('this')}
            >
              <FormControlLabel
                value="this"
                control={<Radio />}
                label={
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <EventNoteIcon fontSize="small" color="primary" />
                      <Typography variant="subtitle1" fontWeight="600">
                        Edit only this occurrence
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Changes will only apply to this specific shift. Other shifts in the series remain unchanged.
                    </Typography>
                  </Box>
                }
                sx={{ 
                  m: 0, 
                  alignItems: 'flex-start',
                  '& .MuiRadio-root': { pt: 0 }
                }}
              />
            </Paper>

            {/* Edit This and Future Occurrences */}
            <Paper 
              elevation={0}
              sx={{
                border: '2px solid',
                borderColor: selectedOption === 'future' ? 'primary.main' : 'divider',
                borderRadius: 2,
                p: 2,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover'
                }
              }}
              onClick={() => setSelectedOption('future')}
            >
              <FormControlLabel
                value="future"
                control={<Radio />}
                label={
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <RepeatIcon fontSize="small" color="primary" />
                      <Typography variant="subtitle1" fontWeight="600">
                        Edit this and all future occurrences
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Changes will apply to this shift and all future shifts in the recurring series.
                    </Typography>
                  </Box>
                }
                sx={{ 
                  m: 0, 
                  alignItems: 'flex-start',
                  '& .MuiRadio-root': { pt: 0 }
                }}
              />
            </Paper>
          </RadioGroup>
        </FormControl>

        <Divider sx={{ my: 2 }} />

        <Box 
          sx={{ 
            p: 2, 
            bgcolor: 'info.light', 
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'info.main'
          }}
        >
          <Typography variant="body2" color="info.dark">
            <strong>Note:</strong> Past occurrences of the recurring series cannot be modified. 
            Only the selected occurrence and future ones can be changed.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={handleCancel}
          variant="outlined"
          size="large"
        >
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm}
          variant="contained"
          size="large"
          sx={{ minWidth: 120 }}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecurringShiftEditModal;